import { AnyResourceDefinition, ErrorOf } from '../resources/types';

export type ResourceErrorMeta = {
  typeName: string;
  instanceKey: string;
  params: unknown;
};

const errorMetaMap = new WeakMap<Error, ResourceErrorMeta>();

export const setResourceErrorMeta = (error: Error, meta: ResourceErrorMeta) => {
  if (!errorMetaMap.has(error)) {
    errorMetaMap.set(error, meta);
  }
};

export const getResourceErrorMeta = (
  error: unknown
): ResourceErrorMeta | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  return errorMetaMap.get(error) ?? null;
};

export const isResourceError = (error: unknown): error is Error =>
  error instanceof Error && errorMetaMap.has(error);

export const getResourceError = <TResource extends AnyResourceDefinition>(
  resource: TResource,
  error: unknown
): ErrorOf<TResource> | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  const meta = errorMetaMap.get(error);

  if (meta && meta.typeName === resource.typeName) {
    return error as ErrorOf<TResource>;
  }

  return null;
};

/**
 * @deprecated Prefer calling `getResourceErrorMeta` to get metadata about the error
 */
export class ResourcePromiseError extends Error {
  typeName: string;
  instanceKey: string;
  params: unknown;

  constructor({ cause, meta }: { cause: Error; meta: ResourceErrorMeta }) {
    super(cause.message, { cause });
    this.typeName = meta.typeName;
    this.instanceKey = meta.instanceKey;
    this.params = meta.params;
  }
}

/**
 * @deprecated Prefer calling `isResourceError` to determine whether an error was thrown
 * by the resource system
 *
 * Warning: This mutates the error object to preserve compat with our one current consumer.
 */
export const isResourcePromiseError = (
  error: unknown
): error is ResourcePromiseError => error instanceof ResourcePromiseError;
